import { useMemo, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CTooltip, Card, SumCard, useIsMobile } from "components/ui/index";
import { MONTHS_FULL, MONTHS_SHORT, TYPE_META } from "constants/index";
import { T } from "theme/index";
import { getInvested, getCurrent, getPortfolioSummary } from "engine/investments";
import { getYearlySummary, getMonthlySummary, getMonthlyBreakdown, getExpenseByCategory, filterByMonth, filterByYear } from "engine/transactions";
import { getBudgetRealization } from "engine/budgets";
import { fmtShort } from "utils/format";

// ── Export helper ─────────────────────────────────────────────────────────────
function exportLaporan(transactions, categories, investments, budgets, year) {
  const wb = XLSX.utils.book_new();
  const yearTxs = filterByYear(transactions, year);  // kept for Excel row loops
  const { income: totalIncome, expense: totalExpense, investasi: totalInvOut, net: netBersih } = getYearlySummary(transactions, year);
  const { totalModal, totalNilai, totalGL } = getPortfolioSummary(investments);
  const savingsRate = totalIncome > 0 ? (totalInvOut + netBersih) / totalIncome : 0;

  // maxMonth: batas bulan yang sudah lewat (0-based index)
  // Hanya tampilkan bulan yang sudah punya data — bulan masa depan = "—"
  const now = new Date();
  const maxMonth = year < now.getFullYear() ? 11
                 : year === now.getFullYear() ? now.getMonth()
                 : -1;

  // ── Style helpers ──────────────────────────────────────────────────────────
  const CLR = {
    darkBg:"0B1222", gold:"E8A21C", green:"22C55E", red:"EF4444",
    blue:"3B82F6", white:"FFFFFF", darkText:"1A202C",
    midBg:"E2E8F0", lightBg:"F7FAFC", section:"2D3748", muted:"718096",
  };
  const mkFill = (rgb) => ({ patternType:"solid", fgColor:{ rgb } });
  const mkFont = ({ sz=10, bold=false, italic=false, color=CLR.darkText } = {}) =>
    ({ name:"Arial", sz, bold, italic, color:{ rgb:color } });
  const mkAln  = ({ h="left", v="center", wrap=false } = {}) =>
    ({ horizontal:h, vertical:v, wrapText:wrap });
  const mkSide = (rgb="CBD5E0", style="thin") => ({ style, color:{ rgb } });
  const mkBdrAll = (rgb="CBD5E0") => { const s=mkSide(rgb); return { top:s,bottom:s,left:s,right:s }; };
  const mkBdrBot = (rgb="CBD5E0") => ({ bottom:mkSide(rgb) });

  // Make a cell: mkC(value, styleObj, numFmt?)
  const mkC = (v, s={}, z=null) => {
    const type = typeof v === "number" ? "n" : "s";
    const cell = { v: v ?? "", t: type, s };
    if (z) cell.z = z;
    return cell;
  };
  const blank = (bg=CLR.white) => mkC("", { fill:mkFill(bg) });

  // Preset style factories
  const sTITLE = (bg=CLR.darkBg, color=CLR.gold, sz=12) => ({
    fill:mkFill(bg), font:mkFont({bold:true,color,sz}), alignment:mkAln({h:"left"})
  });
  const sHDR = (bg=CLR.section) => ({
    fill:mkFill(bg), font:mkFont({bold:true,color:CLR.white,sz:9}),
    alignment:mkAln({h:"left"}), border:mkBdrAll()
  });
  const sCOL = (bg=CLR.midBg) => ({
    fill:mkFill(bg), font:mkFont({bold:true,sz:9}),
    alignment:mkAln({h:"center"}), border:mkBdrAll("B0BEC5")
  });
  const sDATA = (bg=CLR.white, h="left", color=CLR.darkText) => ({
    fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h}), border:mkBdrBot()
  });
  const sNUM  = (bg=CLR.white, color=CLR.darkText) => ({
    fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h:"right"}), border:mkBdrBot()
  });
  const sPCT  = (bg=CLR.white, color=CLR.darkText) => ({
    fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h:"center"}), border:mkBdrBot()
  });
  const sTOT  = (color=CLR.gold, h="right") => ({
    fill:mkFill(CLR.darkBg), font:mkFont({bold:true,color,sz:9}),
    alignment:mkAln({h}), border:mkBdrAll(CLR.gold)
  });

  // Build sheet from rows (2D array of cell objects)
  const buildSheet = (rows, merges=[], cols=[]) => {
    const ws = {};
    let maxC = 0;
    rows.forEach((row, ri) => {
      (row||[]).forEach((cell, ci) => {
        if (!cell) return;
        ws[XLSX.utils.encode_cell({ r:ri, c:ci })] = cell;
        maxC = Math.max(maxC, ci);
      });
    });
    ws["!ref"]    = XLSX.utils.encode_range({ s:{r:0,c:0}, e:{r:rows.length-1, c:maxC} });
    if (merges.length) ws["!merges"] = merges;
    if (cols.length)   ws["!cols"]   = cols;
    return ws;
  };

  const IDR = "#,##0";
  const PCT = "0.0%";
  const n   = (v, s={}, z=IDR)  => mkC(v, s, z);
  const p   = (v, s={})          => mkC(Math.floor((v||0) * 1000) / 1000, s, PCT);  // floor ke 1 desimal
  const B   = (n) => Array(n).fill(null).map(()=>blank(CLR.darkBg));
  const BW  = (n) => Array(n).fill(null).map(()=>blank());

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 1 — RINGKASAN
  // ════════════════════════════════════════════════════════════════════════════
  const cols1 = [{wch:3},{wch:24},{wch:18},{wch:18},{wch:18},{wch:18},{wch:3}];
  const mg1   = [];
  const r1    = [];

  r1.push(B(7));
  mg1.push({s:{r:1,c:1},e:{r:1,c:5}});
  r1.push([blank(CLR.darkBg), mkC("JF FINANCE — LAPORAN KEUANGAN TAHUNAN", sTITLE(CLR.darkBg,CLR.gold,14)), ...B(5)]);
  mg1.push({s:{r:2,c:1},e:{r:2,c:5}});
  r1.push([blank(CLR.darkBg), mkC(`Periode: Tahun ${year}  |  Dibuat otomatis oleh JF Finance`, {fill:mkFill(CLR.darkBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...B(5)]);
  r1.push(B(7));

  // Metric cards
  const metrics = [
    ["💰 TOTAL PEMASUKAN",           totalIncome,  CLR.green],
    ["💸 TOTAL PENGELUARAN",          totalExpense, CLR.red],
    ["📈 ALOKASI INVESTASI THN INI",  totalInvOut,  CLR.gold],
    ["✅ SALDO BERSIH",               netBersih,    netBersih>=0 ? CLR.green : CLR.red],
  ];
  r1.push([blank(CLR.darkBg), ...metrics.map(([lbl,,col]) => mkC(lbl, {fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:8}),alignment:mkAln({h:"center"})})), blank(CLR.darkBg), blank(CLR.darkBg)]);
  r1.push([blank(CLR.darkBg), ...metrics.map(([,val,col]) => n(val, {fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:14}),alignment:mkAln({h:"center"})})), blank(CLR.darkBg), blank(CLR.darkBg)]);
  r1.push(B(7));
  r1.push(BW(7));

  // Alokasi
  mg1.push({s:{r:8,c:1},e:{r:8,c:5}});
  r1.push([blank(), mkC("  📊  ALOKASI PENDAPATAN", sHDR()), ...BW(5)]);
  r1.push([blank(), mkC("Kategori",sCOL()), mkC("Jumlah (Rp)",sCOL()), mkC("% dari Pendapatan",sCOL()), ...BW(3)]);
  [
    ["💸 Pengeluaran",  totalExpense, CLR.red],
    ["📈 Investasi",    totalInvOut,  CLR.gold],
    ["✅ Saldo Bersih", netBersih,    netBersih>=0?CLR.green:CLR.red],
  ].forEach(([lbl,val,col],ai) => {
    const bg = ai%2===0 ? CLR.lightBg : CLR.white;
    r1.push([blank(bg), mkC(lbl,{...sDATA(bg),font:mkFont({bold:true,sz:9})}), n(val,sNUM(bg)), p(totalIncome?val/totalIncome:0,sPCT(bg,col)), ...BW(3)]);
  });
  // Savings Rate row — highlighted
  r1.push([blank(CLR.darkBg), mkC("💡 SAVINGS RATE (Investasi + Saldo Bersih)",{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:9}),alignment:mkAln({h:"left"})}), n(totalInvOut+netBersih,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:9}),alignment:mkAln({h:"right"})}), p(savingsRate,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:10}),alignment:mkAln({h:"center"})}), ...B(3)]);
  r1.push(BW(7));

  // Portfolio
  const portStart = r1.length;
  mg1.push({s:{r:portStart,c:1},e:{r:portStart,c:5}});
  r1.push([blank(), mkC("  💼  NILAI PORTOFOLIO SEKARANG  (semua modal sejak awal, bukan hanya tahun ini)", sHDR()), ...BW(5)]);
  r1.push([blank(), mkC("Instrumen",sCOL()), mkC("Nilai Pasar (Rp)",sCOL()), mkC("Gain/Loss (Rp)",sCOL()), mkC("Return (%)",sCOL()), ...BW(2)]);
  investments.forEach((inv,ii) => {
    const iv=getInvested(inv), cv=getCurrent(inv), gl=cv-iv, rt=iv>0?gl/iv:0;
    const bg = ii%2===0 ? CLR.lightBg : CLR.white;
    r1.push([blank(bg), mkC(inv.name,sDATA(bg)), n(cv,sNUM(bg)), n(gl,{...sNUM(bg),font:mkFont({sz:9,color:gl>=0?CLR.green:CLR.red})}), p(rt,sPCT(bg,rt>=0?CLR.green:CLR.red)), ...BW(2)]);
  });
  r1.push([blank(CLR.darkBg), mkC("TOTAL PORTOFOLIO",sTOT(CLR.gold,"left")), n(totalNilai,sTOT()), n(totalGL,sTOT(totalGL>=0?CLR.green:CLR.red)), p(totalModal?totalGL/totalModal:0,{...sTOT(totalGL>=0?CLR.green:CLR.red),alignment:mkAln({h:"center"})}), ...B(2)]);

  XLSX.utils.book_append_sheet(wb, buildSheet(r1,mg1,cols1), "📋 Ringkasan");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 2 — PEMASUKAN
  // ════════════════════════════════════════════════════════════════════════════
  const cols2 = [{wch:3},{wch:22},{wch:20},{wch:18},{wch:18}];
  const mg2=[], r2=[];

  r2.push(B(5));
  mg2.push({s:{r:1,c:0},e:{r:1,c:4}});
  r2.push([mkC(`  💰  LAPORAN PEMASUKAN — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(4)]);
  r2.push(B(5));
  r2.push([blank(), mkC("Bulan",sCOL()), mkC("Pemasukan (Rp)",sCOL()), mkC("Kumulatif (Rp)",sCOL()), mkC("vs Bulan Lalu",sCOL())]);

  let runInc=0;
  MONTHS_FULL.forEach((m,mi) => {
    const isFuture = mi > maxMonth;
    const v    = isFuture ? null : getMonthlySummary(transactions, mi, year).income;
    const prev = mi > 0 ? getMonthlySummary(transactions, mi-1, year).income : 0;
    const chg  = !isFuture && mi>0 && prev ? (v-prev)/prev : null;
    if (!isFuture) runInc += v;
    const bg = mi%2===0 ? CLR.lightBg : CLR.white;
    const chgCol = chg===null?CLR.darkText:chg>0?CLR.green:CLR.red;
    const dash = mkC("—", {...sDATA(bg,"center"),font:mkFont({sz:9,color:CLR.muted})});
    r2.push([blank(bg), mkC(m,sDATA(bg,"left",isFuture?CLR.muted:CLR.darkText)), isFuture?dash:n(v,sNUM(bg)), isFuture?dash:n(runInc,sNUM(bg)), isFuture?dash:chg!==null?p(chg,sPCT(bg,chgCol)):blank(bg)]);
  });
  r2.push([blank(CLR.darkBg), mkC("TOTAL",sTOT(CLR.gold,"left")), n(totalIncome,sTOT()), ...B(2)]);

  XLSX.utils.book_append_sheet(wb, buildSheet(r2,mg2,cols2), "💰 Pemasukan");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 3 — PENGELUARAN
  // ════════════════════════════════════════════════════════════════════════════
  const catData3 = categories.filter(c=>c.type==="expense").map(cat => {
    const monthly = MONTHS_FULL.map((_,mi) =>
      filterByMonth(transactions, mi, year).filter(t=>t.type==="expense"&&t.categoryId===cat.id).reduce((a,t)=>a+t.amount,0)
    );
    const total = monthly.reduce((a,v)=>a+v,0);
    return { cat, monthly, total };
  }).filter(x=>x.total>0).sort((a,b)=>b.total-a.total);

  const activeMonths = Math.min(maxMonth + 1, 12); // jumlah bulan yang sudah lewat
  const cols3 = [{wch:3},{wch:24},...Array(activeMonths).fill({wch:13}),{wch:15}];
  const mg3=[], r3=[];

  r3.push(B(2+activeMonths));
  mg3.push({s:{r:1,c:0},e:{r:1,c:1+activeMonths}});
  r3.push([mkC(`  💸  LAPORAN PENGELUARAN — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(1+activeMonths)]);
  r3.push(B(2+activeMonths));
  mg3.push({s:{r:3,c:0},e:{r:3,c:1+activeMonths}});
  r3.push([mkC("  Pengeluaran per Kategori per Bulan", sHDR()), ...Array(1+activeMonths).fill(blank(CLR.section))]);
  r3.push([blank(), mkC("Kategori",sCOL()), ...MONTHS_SHORT.slice(0,activeMonths).map(m=>mkC(m,sCOL())), mkC("TOTAL",sCOL())]);

  catData3.forEach(({cat,monthly,total},ci) => {
    const bg = ci%2===0 ? CLR.lightBg : CLR.white;
    r3.push([blank(bg), mkC(`${cat.icon} ${cat.name}`,sDATA(bg)), ...monthly.slice(0,activeMonths).map(v=>n(v,sNUM(bg))), n(total,{...sNUM(bg),font:mkFont({sz:9,bold:true,color:CLR.red})})]);
  });
  const monthTotals = Array.from({length:activeMonths},(_,mi)=>catData3.reduce((a,{monthly})=>a+monthly[mi],0));
  r3.push([blank(CLR.darkBg), mkC("TOTAL BULANAN",sTOT(CLR.gold,"left")), ...monthTotals.map(v=>n(v,sTOT())), n(totalExpense,sTOT())]);

  r3.push(BW(2+activeMonths));
  const sec3 = r3.length;
  mg3.push({s:{r:sec3,c:0},e:{r:sec3,c:4}});
  r3.push([mkC("  🏆  KATEGORI PENGELUARAN TERBESAR", sHDR()), ...Array(4).fill(blank(CLR.section)), ...BW(activeMonths-3)]);
  r3.push([blank(), mkC("Kategori",sCOL()), mkC("Total (Rp)",sCOL()), mkC("% Pengeluaran",sCOL()), mkC("Peringkat",sCOL()), ...BW(activeMonths-3)]);
  catData3.forEach(({cat,total},ci) => {
    const bg = ci%2===0 ? CLR.lightBg : CLR.white;
    const pct = totalExpense>0 ? total/totalExpense : 0;
    r3.push([blank(bg), mkC(`${cat.icon} ${cat.name}`,sDATA(bg)), n(total,{...sNUM(bg),font:mkFont({sz:9,color:ci===0?CLR.red:CLR.darkText,bold:ci===0})}), p(pct,sPCT(bg)), mkC(`#${ci+1}`,{...sDATA(bg),alignment:mkAln({h:"center"})}), ...BW(activeMonths-3)]);
  });

  XLSX.utils.book_append_sheet(wb, buildSheet(r3,mg3,cols3), "💸 Pengeluaran");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 4 — INVESTASI
  // ════════════════════════════════════════════════════════════════════════════
  const cols4 = [{wch:3},{wch:28},{wch:16},{wch:14},{wch:18},{wch:18},{wch:16},{wch:14}];
  const mg4=[], r4=[];

  r4.push(B(8));
  mg4.push({s:{r:1,c:0},e:{r:1,c:7}});
  r4.push([mkC(`  📈  LAPORAN INVESTASI & PORTOFOLIO — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(7)]);
  r4.push(B(8));

  // Summary metric row
  const invMetrics = [["Total Modal",CLR.blue,totalModal,false],["Nilai Sekarang",CLR.gold,totalNilai,false],["Total Gain/Loss",totalGL>=0?CLR.green:CLR.red,totalGL,false],["Return Keseluruhan",totalGL>=0?CLR.green:CLR.red,totalModal?totalGL/totalModal:0,true]];
  r4.push([blank(CLR.darkBg), ...invMetrics.map(([lbl])=>mkC(lbl,{fill:mkFill("2D3748"),font:mkFont({bold:true,color:CLR.white,sz:8}),alignment:mkAln({h:"center"})})), ...B(3)]);
  r4.push([blank(CLR.darkBg), ...invMetrics.map(([,col,val,isPct])=> isPct ? p(val,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:13}),alignment:mkAln({h:"center"})}) : n(val,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:13}),alignment:mkAln({h:"center"})})), ...B(3)]);
  r4.push(B(8));
  r4.push(BW(8));

  mg4.push({s:{r:r4.length,c:0},e:{r:r4.length,c:7}});
  r4.push([mkC("  Detail Instrumen Investasi", sHDR()), ...Array(7).fill(blank(CLR.section))]);
  r4.push([blank(), mkC("Nama Instrumen",sCOL()), mkC("Kelompok",sCOL()), mkC("Tipe",sCOL()), mkC("Modal (Rp)",sCOL()), mkC("Nilai Pasar (Rp)",sCOL()), mkC("Gain/Loss (Rp)",sCOL()), mkC("Return (%)",sCOL())]);
  investments.forEach((inv,ii) => {
    const iv=getInvested(inv), cv=getCurrent(inv), gl=cv-iv, rt=iv>0?gl/iv:0;
    const meta=TYPE_META[inv.type]||TYPE_META.lainnya;
    const bg = ii%2===0 ? CLR.lightBg : CLR.white;
    r4.push([blank(bg), mkC(inv.name,sDATA(bg)), mkC(inv.group,sDATA(bg)), mkC(meta.label,{...sDATA(bg),alignment:mkAln({h:"center"})}), n(iv,sNUM(bg)), n(cv,sNUM(bg)), n(gl,{...sNUM(bg),font:mkFont({sz:9,color:gl>=0?CLR.green:CLR.red})}), p(rt,sPCT(bg,rt>=0?CLR.green:CLR.red))]);
  });
  r4.push([blank(CLR.darkBg), mkC("TOTAL PORTOFOLIO",sTOT(CLR.gold,"left")), ...B(2), n(totalModal,sTOT()), n(totalNilai,sTOT()), n(totalGL,sTOT(totalGL>=0?CLR.green:CLR.red)), p(totalModal?totalGL/totalModal:0,{...sTOT(totalGL>=0?CLR.green:CLR.red),alignment:mkAln({h:"center"})})]);

  r4.push(BW(8));
  mg4.push({s:{r:r4.length,c:0},e:{r:r4.length,c:4}});
  r4.push([mkC("  Realisasi Investasi per Bulan", sHDR()), ...Array(4).fill(blank(CLR.section)), ...BW(3)]);
  r4.push([blank(), mkC("Bulan",sCOL()), mkC("Diinvestasikan (Rp)",sCOL()), mkC("Kumulatif (Rp)",sCOL()), mkC("% dari Pendapatan",sCOL()), ...BW(3)]);
  let runInv=0;
  MONTHS_FULL.forEach((m,mi) => {
    const isFuture = mi > maxMonth;
    const v   = isFuture ? null : getMonthlySummary(transactions, mi, year).investasi;
    const inc = isFuture ? 0 : getMonthlySummary(transactions, mi, year).income;
    if (!isFuture) runInv += v;
    const bg = mi%2===0 ? CLR.lightBg : CLR.white;
    const dash = mkC("—", {...sDATA(bg,"center"),font:mkFont({sz:9,color:CLR.muted})});
    r4.push([blank(bg), mkC(m,sDATA(bg,"left",isFuture?CLR.muted:CLR.darkText)), isFuture?dash:n(v,sNUM(bg)), isFuture?dash:n(runInv,sNUM(bg)), isFuture?dash:p(inc?v/inc:0,sPCT(bg,v>0?CLR.gold:CLR.muted)), ...BW(3)]);
  });
  r4.push([blank(CLR.darkBg), mkC("TOTAL",sTOT(CLR.gold,"left")), n(totalInvOut,sTOT()), ...B(1), p(totalIncome?totalInvOut/totalIncome:0,{...sTOT(),alignment:mkAln({h:"center"})}), ...B(3)]);

  r4.push(BW(8));

  // ── Asset Allocation Per Instrumen ──
  const instrSorted = [...investments]
    .map(inv => ({ inv, nilai:getCurrent(inv), modal:getInvested(inv) }))
    .sort((a,b) => b.nilai - a.nilai);

  mg4.push({s:{r:r4.length,c:0},e:{r:r4.length,c:7}});
  r4.push([mkC("  🔍  ASSET ALLOCATION — Per Instrumen (% dari Total Portofolio)", sHDR()), ...Array(7).fill(blank(CLR.section))]);
  r4.push([blank(), mkC("Instrumen",sCOL()), mkC("Tipe",sCOL()), mkC("Kelompok",sCOL()), mkC("Nilai Pasar (Rp)",sCOL()), mkC("Gain/Loss (Rp)",sCOL()), mkC("Alokasi (%)",sCOL()), mkC("Proporsi",sCOL())]);

  instrSorted.forEach(({inv, nilai, modal}, ii) => {
    const gl   = nilai - modal;
    const alok = totalNilai > 0 ? nilai / totalNilai : 0;
    const barLen = Math.round(alok * 30); // max 30 chars
    const bar  = "█".repeat(barLen) + "░".repeat(30 - barLen);
    const bg   = ii%2===0 ? CLR.lightBg : CLR.white;
    const meta = TYPE_META[inv.type] || TYPE_META.lainnya;
    const col  = meta.color ? meta.color.replace("#","") : "6B7280";
    r4.push([
      blank(bg),
      mkC(inv.name, {...sDATA(bg), font:mkFont({sz:9, bold:true})}),
      mkC(meta.label, {...sDATA(bg,"center"), font:mkFont({sz:9, color:col})}),
      mkC(inv.group || "—", sDATA(bg,"left",CLR.muted)),
      n(nilai, sNUM(bg)),
      n(gl, {...sNUM(bg), font:mkFont({sz:9, color:gl>=0?CLR.green:CLR.red})}),
      p(alok, {...sPCT(bg), font:mkFont({sz:9, bold:true, color:alok>=0.2?CLR.gold:CLR.darkText})}),
      mkC(bar, {fill:mkFill(bg), font:{name:"Courier New", sz:7, color:{rgb:col}}, alignment:mkAln({h:"left"}), border:mkBdrBot()}),
    ]);
  });
  r4.push([blank(CLR.darkBg), mkC(`TOTAL ${instrSorted.length} INSTRUMEN`,sTOT(CLR.gold,"left")), blank(CLR.darkBg), blank(CLR.darkBg), n(totalNilai,sTOT()), n(totalGL,sTOT(totalGL>=0?CLR.green:CLR.red)), p(1,{...sTOT(),alignment:mkAln({h:"center"})}), blank(CLR.darkBg)]);

  r4.push(BW(8));

  // ── Asset Allocation by Tipe ──
  const byType = {};
  investments.forEach(inv => {
    const key  = TYPE_META[inv.type]?.label || inv.type;
    if (!byType[key]) byType[key] = { nilai:0, modal:0 };
    byType[key].nilai += getCurrent(inv);
    byType[key].modal += getInvested(inv);
  });
  const byTypeSorted = Object.entries(byType).sort((a,b) => b[1].nilai - a[1].nilai);

  const typeColors = { "Saham IDX":CLR.green, "Saham US":CLR.blue, "Crypto":CLR.orange, "RDPU":CLR.purple, "RDPT":"8B5CF6", "Emas":CLR.gold, "Forex":"10B981", "Lainnya":"6B7280" };
  const typeColorOf = (label) => typeColors[label] || "6B7280";

  const allocHdr = [blank(), mkC("Kategori",sCOL()), mkC("Nilai Pasar (Rp)",sCOL()), mkC("Modal (Rp)",sCOL()), mkC("Gain/Loss (Rp)",sCOL()), mkC("Return (%)",sCOL()), mkC("Alokasi (%)",sCOL()), mkC("Proporsi",sCOL())];

  mg4.push({s:{r:r4.length,c:0},e:{r:r4.length,c:7}});
  r4.push([mkC("  📊  ASSET ALLOCATION — Berdasarkan Tipe Aset", sHDR()), ...Array(7).fill(blank(CLR.section))]);
  r4.push(allocHdr);

  byTypeSorted.forEach(([tipe, data], ti) => {
    const gl   = data.nilai - data.modal;
    const ret  = data.modal > 0 ? gl / data.modal : 0;
    const alok = totalNilai > 0 ? data.nilai / totalNilai : 0;
    const barLen = Math.round(alok * 20);
    const bar  = "█".repeat(barLen) + "░".repeat(20 - barLen);
    const bg   = ti%2===0 ? CLR.lightBg : CLR.white;
    const col  = typeColorOf(tipe);
    r4.push([
      blank(bg),
      mkC(tipe, {...sDATA(bg), font:mkFont({sz:9, bold:true, color:col})}),
      n(data.nilai, sNUM(bg)),
      n(data.modal, sNUM(bg)),
      n(gl,  {...sNUM(bg), font:mkFont({sz:9, color:gl>=0?CLR.green:CLR.red})}),
      p(ret, sPCT(bg, ret>=0?CLR.green:CLR.red)),
      p(alok,{...sPCT(bg, col), font:mkFont({sz:9, bold:true, color:col})}),
      mkC(bar, {fill:mkFill(bg), font:{name:"Courier New", sz:8, color:{rgb:col}}, alignment:mkAln({h:"left"}), border:mkBdrBot()}),
    ]);
  });
  r4.push([blank(CLR.darkBg), mkC("TOTAL",sTOT(CLR.gold,"left")), n(totalNilai,sTOT()), n(totalModal,sTOT()), n(totalGL,sTOT(totalGL>=0?CLR.green:CLR.red)), p(totalModal?totalGL/totalModal:0,{...sTOT(totalGL>=0?CLR.green:CLR.red),alignment:mkAln({h:"center"})}), p(1,{...sTOT(),alignment:mkAln({h:"center"})}), blank(CLR.darkBg)]);

  r4.push(BW(8));

  // ── Asset Allocation by Kelompok ──
  const byGroup = {};
  investments.forEach(inv => {
    const key = inv.group || "Lainnya";
    if (!byGroup[key]) byGroup[key] = { nilai:0, modal:0 };
    byGroup[key].nilai += getCurrent(inv);
    byGroup[key].modal += getInvested(inv);
  });
  const byGroupSorted = Object.entries(byGroup).sort((a,b) => b[1].nilai - a[1].nilai);
  const grpColors = [CLR.blue, CLR.green, CLR.orange, CLR.purple, CLR.gold, CLR.red, "10B981", "06B6D4"];

  mg4.push({s:{r:r4.length,c:0},e:{r:r4.length,c:7}});
  r4.push([mkC("  🗂️  ASSET ALLOCATION — Berdasarkan Kelompok / Portfolio", sHDR()), ...Array(7).fill(blank(CLR.section))]);
  r4.push(allocHdr);

  byGroupSorted.forEach(([grp, data], gi) => {
    const gl   = data.nilai - data.modal;
    const ret  = data.modal > 0 ? gl / data.modal : 0;
    const alok = totalNilai > 0 ? data.nilai / totalNilai : 0;
    const barLen = Math.round(alok * 20);
    const bar  = "█".repeat(barLen) + "░".repeat(20 - barLen);
    const bg   = gi%2===0 ? CLR.lightBg : CLR.white;
    const col  = grpColors[gi % grpColors.length];
    r4.push([
      blank(bg),
      mkC(grp, {...sDATA(bg), font:mkFont({sz:9, bold:true, color:col})}),
      n(data.nilai, sNUM(bg)),
      n(data.modal, sNUM(bg)),
      n(gl,  {...sNUM(bg), font:mkFont({sz:9, color:gl>=0?CLR.green:CLR.red})}),
      p(ret, sPCT(bg, ret>=0?CLR.green:CLR.red)),
      p(alok,{...sPCT(bg, col), font:mkFont({sz:9, bold:true, color:col})}),
      mkC(bar, {fill:mkFill(bg), font:{name:"Courier New", sz:8, color:{rgb:col}}, alignment:mkAln({h:"left"}), border:mkBdrBot()}),
    ]);
  });
  r4.push([blank(CLR.darkBg), mkC("TOTAL",sTOT(CLR.gold,"left")), n(totalNilai,sTOT()), n(totalModal,sTOT()), n(totalGL,sTOT(totalGL>=0?CLR.green:CLR.red)), p(totalModal?totalGL/totalModal:0,{...sTOT(totalGL>=0?CLR.green:CLR.red),alignment:mkAln({h:"center"})}), p(1,{...sTOT(),alignment:mkAln({h:"center"})}), blank(CLR.darkBg)]);

  // Update col widths to fit bar column
  cols4.push({wch:22});

  XLSX.utils.book_append_sheet(wb, buildSheet(r4,mg4,cols4), "📈 Investasi");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 5 — BUDGET VS AKTUAL
  // ════════════════════════════════════════════════════════════════════════════
  const bvaData  = (() => {
    const catMap = {};
    for (let mi = 0; mi < 12; mi++) {
      const mData = getBudgetRealization(budgets, transactions, categories, [], mi, year);
      mData.forEach(d => {
        if (!catMap[d.id]) catMap[d.id] = { cat: d, budgetTotal: 0, aktualTotal: 0 };
        catMap[d.id].budgetTotal += d.budget;
        catMap[d.id].aktualTotal += d.actual;
      });
    }
    return Object.values(catMap).map(({ cat, budgetTotal, aktualTotal }) => ({
      cat, budgetTotal, aktualTotal,
      selisih: budgetTotal - aktualTotal,
      pctRealisasi: budgetTotal > 0 ? aktualTotal / budgetTotal : null,
    })).filter(d => d.budgetTotal > 0 || d.aktualTotal > 0);
  })();

  const expBva = bvaData.filter(d => d.cat.type === "expense");
  const invBva = bvaData.filter(d => d.cat.type === "investasi");

  const cols5 = [{wch:3},{wch:26},{wch:18},{wch:18},{wch:18},{wch:16},{wch:18}];
  const mg5=[], r5=[];

  r5.push(B(7));
  mg5.push({s:{r:1,c:0},e:{r:1,c:6}});
  r5.push([mkC(`  📊  BUDGET VS AKTUAL — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(6)]);
  r5.push(B(7));

  const bvaColHdr = [blank(), mkC("Kategori",sCOL()), mkC("Budget Tahunan (Rp)",sCOL()), mkC("Aktual (Rp)",sCOL()), mkC("Selisih (Rp)",sCOL()), mkC("% Realisasi",sCOL()), mkC("Status",sCOL())];
  const bvaRow = (d, bi, isInv=false) => {
    const bg  = bi%2===0 ? CLR.lightBg : CLR.white;
    const noB  = d.budgetTotal === 0;
    const pct  = Math.floor((d.pctRealisasi||0) * 1000) / 1000;  // floor sebelum comparison
    const over = d.selisih < 0;  // aktual > budget
    // Investasi: lebih banyak invest = bagus. Expense: lebih banyak keluar = buruk.
    let status, stCol, selisihCol, pctCol;
    if (noB) {
      status = "Tidak Dianggarkan"; stCol = CLR.muted; selisihCol = CLR.muted; pctCol = CLR.muted;
    } else if (isInv) {
      // Investasi logic (kebalikan expense)
      status   = over ? "📈 Di Atas Target" : pct >= 1.0 ? "✅ Sesuai Target" : pct >= 0.9 ? "✅ On Track" : "⚠️ Di Bawah Target";
      stCol    = over ? CLR.green : pct >= 0.9 ? CLR.green : "F59E0B";
      selisihCol = over ? CLR.green : CLR.red;  // selisih negatif (over) = hijau untuk investasi
      pctCol   = over ? CLR.green : pct >= 0.9 ? CLR.green : "F59E0B";
    } else {
      // Expense logic (normal)
      status   = over ? "⚠️ Over Budget" : pct >= 1.0 ? "✅ Tepat Anggaran" : pct >= 0.9 ? "🟡 Hampir Habis" : "✅ On Track";
      stCol    = over ? CLR.red : pct >= 0.9 ? "F59E0B" : CLR.green;
      selisihCol = over ? CLR.red : CLR.green;
      pctCol   = over ? CLR.red : pct >= 0.9 ? "F59E0B" : CLR.green;
    }
    return [blank(bg), mkC(`${d.cat.icon} ${d.cat.name}`, sDATA(bg)), n(d.budgetTotal, sNUM(bg)), n(d.aktualTotal, sNUM(bg)), n(d.selisih, {...sNUM(bg), font:mkFont({sz:9, color:selisihCol})}), pct!==null ? p(pct, sPCT(bg, pctCol)) : mkC("—",{...sDATA(bg,"center"),font:mkFont({sz:9,color:CLR.muted})}), mkC(status, {...sDATA(bg), font:mkFont({sz:9, color:stCol, bold:true})})];
  };

  // Section: Pengeluaran
  const sec5a = r5.length;
  mg5.push({s:{r:sec5a,c:0},e:{r:sec5a,c:6}});
  r5.push([mkC("  💸  ANGGARAN PENGELUARAN", sHDR()), ...Array(6).fill(blank(CLR.section))]);
  r5.push(bvaColHdr);
  expBva.forEach((d,bi) => r5.push(bvaRow(d, bi)));
  const expBvaBudget = expBva.reduce((a,d)=>a+d.budgetTotal,0);
  const expBvaAktual = expBva.reduce((a,d)=>a+d.aktualTotal,0);
  r5.push([blank(CLR.darkBg), mkC("TOTAL PENGELUARAN",sTOT(CLR.gold,"left")), n(expBvaBudget,sTOT()), n(expBvaAktual,sTOT()), n(expBvaBudget-expBvaAktual,sTOT(expBvaBudget-expBvaAktual>=0?CLR.green:CLR.red)), expBvaBudget>0?p(expBvaAktual/expBvaBudget,{...sTOT(expBvaAktual>expBvaBudget?CLR.red:CLR.green),alignment:mkAln({h:"center"})}):blank(CLR.darkBg), blank(CLR.darkBg)]);

  r5.push(BW(7));

  // Section: Investasi
  const sec5b = r5.length;
  mg5.push({s:{r:sec5b,c:0},e:{r:sec5b,c:6}});
  r5.push([mkC("  📈  ANGGARAN INVESTASI", sHDR()), ...Array(6).fill(blank(CLR.section))]);
  r5.push(bvaColHdr);
  invBva.forEach((d,bi) => r5.push(bvaRow(d, bi, true)));
  const invBvaBudget = invBva.reduce((a,d)=>a+d.budgetTotal,0);
  const invBvaAktual = invBva.reduce((a,d)=>a+d.aktualTotal,0);
  r5.push([blank(CLR.darkBg), mkC("TOTAL INVESTASI",sTOT(CLR.gold,"left")), n(invBvaBudget,sTOT()), n(invBvaAktual,sTOT()), n(invBvaBudget-invBvaAktual,sTOT(invBvaBudget-invBvaAktual<=0?CLR.green:CLR.red)), invBvaBudget>0?p(invBvaAktual/invBvaBudget,{...sTOT(invBvaAktual>invBvaBudget?CLR.green:CLR.red),alignment:mkAln({h:"center"})}):blank(CLR.darkBg), blank(CLR.darkBg)]);

  if (expBva.length === 0 && invBva.length === 0) {
    r5.push(BW(7));
    mg5.push({s:{r:r5.length,c:0},e:{r:r5.length,c:6}});
    r5.push([mkC("  ℹ️  Belum ada anggaran yang diatur. Atur anggaran di menu Anggaran terlebih dahulu.", {fill:mkFill(CLR.lightBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...BW(6)]);
  }

  XLSX.utils.book_append_sheet(wb, buildSheet(r5,mg5,cols5), "📊 Budget vs Aktual");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 6 — TREND PENGELUARAN
  // ════════════════════════════════════════════════════════════════════════════
  const cols6 = [{wch:3},{wch:22},{wch:18},{wch:18},{wch:16},{wch:16},{wch:20}];
  const mg6=[], r6=[];

  r6.push(B(7));
  mg6.push({s:{r:1,c:0},e:{r:1,c:6}});
  r6.push([mkC(`  📉  TREND PENGELUARAN BULANAN — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(6)]);
  mg6.push({s:{r:2,c:0},e:{r:2,c:6}});
  r6.push([mkC(`  Perbandingan pengeluaran tiap bulan untuk melihat apakah kamu semakin boros atau hemat`, {fill:mkFill(CLR.darkBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...B(6)]);
  r6.push(B(7));

  // Section 1: Trend keseluruhan (total pengeluaran per bulan)
  const sec6a = r6.length;
  mg6.push({s:{r:sec6a,c:0},e:{r:sec6a,c:6}});
  r6.push([mkC("  Trend Total Pengeluaran per Bulan", sHDR()), ...Array(6).fill(blank(CLR.section))]);
  r6.push([blank(), mkC("Bulan",sCOL()), mkC("Pengeluaran (Rp)",sCOL()), mkC("vs Bulan Lalu (Rp)",sCOL()), mkC("% Perubahan",sCOL()), mkC("vs Rata-rata (Rp)",sCOL()), mkC("Status",sCOL())]);

  const monthlyExp = MONTHS_FULL.map((_,mi) =>
    mi > maxMonth ? null : getMonthlySummary(transactions, mi, year).expense
  );
  const validExp   = monthlyExp.filter(v => v !== null);
  const avgExp     = validExp.length > 0 ? validExp.reduce((a,v)=>a+v,0) / validExp.length : 0;

  MONTHS_FULL.forEach((m, mi) => {
    const isFuture = mi > maxMonth;
    const bg = mi%2===0 ? CLR.lightBg : CLR.white;
    const dash = mkC("—", {...sDATA(bg,"center"), font:mkFont({sz:9,color:CLR.muted})});

    if (isFuture) {
      r6.push([blank(bg), mkC(m, sDATA(bg,"left",CLR.muted)), dash, dash, dash, dash, dash]);
      return;
    }

    const v    = monthlyExp[mi];
    const prev = mi > 0 ? monthlyExp[mi-1] : null;
    const diff     = prev !== null && prev !== null ? v - prev : null;
    const pctChg   = prev !== null && prev > 0 ? diff / prev : null;
    const vsAvg    = v - avgExp;
    const isBoros  = diff !== null && diff > 0;
    const isHiding = diff !== null && diff < 0;
    const status   = diff === null ? "—" : isBoros ? "📈 Lebih Boros" : isHiding ? "📉 Lebih Hemat" : "➡️ Sama";
    const stCol    = diff === null ? CLR.muted : isBoros ? CLR.red : CLR.green;

    r6.push([
      blank(bg),
      mkC(m, sDATA(bg)),
      n(v, sNUM(bg)),
      diff !== null ? n(diff, {...sNUM(bg), font:mkFont({sz:9, color:isBoros?CLR.red:CLR.green})}) : dash,
      pctChg !== null ? p(pctChg, sPCT(bg, isBoros?CLR.red:CLR.green)) : dash,
      n(vsAvg, {...sNUM(bg), font:mkFont({sz:9, color:vsAvg>0?CLR.red:CLR.green})}),
      mkC(status, {...sDATA(bg), font:mkFont({sz:9, color:stCol, bold:true})}),
    ]);
  });

  // Rata-rata row
  r6.push([blank(CLR.darkBg), mkC("RATA-RATA BULANAN", sTOT(CLR.gold,"left")), n(avgExp, sTOT()), ...B(4), blank(CLR.darkBg)]);

  r6.push(BW(7));

  // Section 2: Trend per kategori (top 5 terbesar, naik atau turun)
  const sec6b = r6.length;
  mg6.push({s:{r:sec6b,c:0},e:{r:sec6b,c:6}});
  r6.push([mkC("  Top Kategori — Perubahan Terbesar (Bulan Terakhir vs Sebelumnya)", sHDR()), ...Array(6).fill(blank(CLR.section))]);
  r6.push([blank(), mkC("Kategori",sCOL()), mkC("Bulan Terakhir (Rp)",sCOL()), mkC("Bulan Sebelumnya (Rp)",sCOL()), mkC("Selisih (Rp)",sCOL()), mkC("% Perubahan",sCOL()), mkC("Trend",sCOL())]);

  // Last two active months
  const lastMi = maxMonth;
  const prevMi = maxMonth - 1;
  if (lastMi >= 1) {
    const catTrends = categories.filter(c=>c.type==="expense").map(cat => {
      const last = filterByMonth(transactions, lastMi, year).filter(t=>t.type==="expense"&&t.categoryId===cat.id).reduce((a,t)=>a+t.amount,0);
      const prev = filterByMonth(transactions, prevMi, year).filter(t=>t.type==="expense"&&t.categoryId===cat.id).reduce((a,t)=>a+t.amount,0);
      return { cat, last, prev, diff: last - prev };
    }).filter(d => d.last > 0 || d.prev > 0).sort((a,b) => Math.abs(b.diff) - Math.abs(a.diff)).slice(0, 8);

    catTrends.forEach((d, ci) => {
      const bg   = ci%2===0 ? CLR.lightBg : CLR.white;
      const up   = d.diff > 0;
      const pct  = d.prev > 0 ? d.diff / d.prev : null;
      const trend = d.diff === 0 ? "➡️ Stabil" : up ? "📈 Naik" : "📉 Turun";
      r6.push([blank(bg), mkC(`${d.cat.icon} ${d.cat.name}`, sDATA(bg)), n(d.last, sNUM(bg)), n(d.prev, sNUM(bg)), n(d.diff, {...sNUM(bg), font:mkFont({sz:9, color:up?CLR.red:CLR.green})}), pct!==null ? p(pct, sPCT(bg, up?CLR.red:CLR.green)) : mkC("Baru",{...sDATA(bg,"center"),font:mkFont({sz:9,color:CLR.gold})}), mkC(trend, {...sDATA(bg), font:mkFont({sz:9, color:up?CLR.red:CLR.green, bold:true})})]);
    });

    // Label bulan yang dibandingkan
    r6.push(BW(7));
    mg6.push({s:{r:r6.length,c:0},e:{r:r6.length,c:6}});
    r6.push([mkC(`  ℹ️  Perbandingan antara ${MONTHS_FULL[lastMi]} (bulan terakhir) vs ${MONTHS_FULL[prevMi]} (bulan sebelumnya)`, {fill:mkFill(CLR.lightBg),font:mkFont({sz:8,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...BW(6)]);
  }

  XLSX.utils.book_append_sheet(wb, buildSheet(r6,mg6,cols6), "📉 Trend Pengeluaran");

  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 7 — HISTORY TRANSAKSI
  // ════════════════════════════════════════════════════════════════════════════
  const cols7 = [{wch:3},{wch:13},{wch:14},{wch:22},{wch:28},{wch:30},{wch:3}];
  const mg7=[], r7=[];

  r7.push(B(7));
  mg7.push({s:{r:1,c:0},e:{r:1,c:5}});
  r7.push([blank(CLR.darkBg), mkC(`  📜  HISTORY TRANSAKSI — ${year}`, sTITLE(CLR.darkBg,CLR.gold,12)), ...B(5)]);
  mg7.push({s:{r:2,c:0},e:{r:2,c:5}});
  r7.push([blank(CLR.darkBg), mkC(`  Seluruh transaksi tercatat sepanjang tahun ${year}, urut dari tanggal terbaru`, {fill:mkFill(CLR.darkBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...B(5)]);
  r7.push(B(7));

  r7.push([blank(), mkC("Tanggal",sCOL()), mkC("Tipe",sCOL()), mkC("Kategori",sCOL()), mkC("Deskripsi",sCOL()), mkC("Catatan",sCOL()), mkC("Jumlah (Rp)",sCOL())]);
  // fix kolom jumlah lebih lebar di kanan — geser urutan agar nominal di akhir tetap rapi
  // (kita susun ulang header agar Jumlah ada di kolom terakhir, sesuai data row di bawah)

  const typeMeta = {
    income:    { label:"💰 Masuk",     color:CLR.green },
    expense:   { label:"💸 Keluar",    color:CLR.red   },
    transfer:  { label:"🔄 Transfer",  color:CLR.blue  },
    investasi: { label:"📈 Investasi", color:CLR.gold  },
  };

  const sortedTxs = [...yearTxs].sort((a,b) => new Date(b.date) - new Date(a.date));
  sortedTxs.forEach((t, ti) => {
    const bg   = ti%2===0 ? CLR.lightBg : CLR.white;
    const cat  = categories.find(c => c.id === t.categoryId);
    const tm   = typeMeta[t.type] || typeMeta.expense;
    const catLabel = t.type === "transfer" ? "—" : cat ? `${cat.icon} ${cat.name}` : "—";
    const dateLabel = new Date(t.date).toLocaleDateString("id-ID", { day:"2-digit", month:"short", year:"numeric" });
    const sign = t.type === "income" ? "+" : t.type === "transfer" ? "⇄" : "-";

    r7.push([
      blank(bg),
      mkC(dateLabel, sDATA(bg)),
      mkC(tm.label, {...sDATA(bg,"center"), font:mkFont({sz:9, bold:true, color:tm.color})}),
      mkC(catLabel, sDATA(bg)),
      mkC(t.description || "—", sDATA(bg)),
      mkC(t.notes || "—", {...sDATA(bg), font:mkFont({sz:9, italic:!t.notes, color:t.notes?CLR.darkText:CLR.muted})}),
      mkC(`${sign} ${Number(t.amount).toLocaleString("id-ID")}`, {fill:mkFill(bg), font:mkFont({sz:9, bold:true, color:tm.color}), alignment:mkAln({h:"right"}), border:mkBdrBot()}),
    ]);
  });

  r7.push([blank(CLR.darkBg), mkC(`TOTAL ${sortedTxs.length} TRANSAKSI`,{...sTOT(CLR.gold,"left")}), ...B(5)]);

  XLSX.utils.book_append_sheet(wb, buildSheet(r7,mg7,cols7), "📜 History Transaksi");

  XLSX.writeFile(wb, `JF_Finance_Laporan_${year}.xlsx`);
}

// ── Export Bulanan ────────────────────────────────────────────────────────────
function exportLaporanBulanan(transactions, categories, investments, budgets, month, year) {
  const wb     = XLSX.utils.book_new();
  const mTxs   = filterByMonth(transactions, month, year);   // kept for Excel row loops
  const mLabel = `${MONTHS_FULL[month]} ${year}`;
  const { income: totalIncome, expense: totalExpense, investasi: totalInvOut, net: netBersih } = getMonthlySummary(transactions, month, year);
  const savingsRate = totalIncome > 0 ? (totalInvOut + netBersih) / totalIncome : 0;

  const CLR = { darkBg:"0B1222", gold:"E8A21C", green:"22C55E", red:"EF4444", blue:"3B82F6", white:"FFFFFF", darkText:"1A202C", midBg:"E2E8F0", lightBg:"F7FAFC", section:"2D3748", muted:"718096" };
  const mkFill = (rgb) => ({ patternType:"solid", fgColor:{ rgb } });
  const mkFont = ({ sz=10, bold=false, italic=false, color=CLR.darkText } = {}) => ({ name:"Arial", sz, bold, italic, color:{ rgb:color } });
  const mkAln  = ({ h="left", v="center", wrap=false } = {}) => ({ horizontal:h, vertical:v, wrapText:wrap });
  const mkSide = (rgb="CBD5E0", style="thin") => ({ style, color:{ rgb } });
  const mkBdrAll = (rgb="CBD5E0") => { const s=mkSide(rgb); return { top:s,bottom:s,left:s,right:s }; };
  const mkBdrBot = (rgb="CBD5E0") => ({ bottom:mkSide(rgb) });
  const mkC = (v, s={}, z=null) => { const cell={v:v??"",t:typeof v==="number"?"n":"s",s}; if(z) cell.z=z; return cell; };
  const blank  = (bg=CLR.white) => mkC("", { fill:mkFill(bg) });
  const sTITLE = (bg=CLR.darkBg, color=CLR.gold, sz=12) => ({ fill:mkFill(bg), font:mkFont({bold:true,color,sz}), alignment:mkAln({h:"left"}) });
  const sHDR   = (bg=CLR.section) => ({ fill:mkFill(bg), font:mkFont({bold:true,color:CLR.white,sz:9}), alignment:mkAln({h:"left"}), border:mkBdrAll() });
  const sCOL   = (bg=CLR.midBg)   => ({ fill:mkFill(bg), font:mkFont({bold:true,sz:9}), alignment:mkAln({h:"center"}), border:mkBdrAll("B0BEC5") });
  const sDATA  = (bg=CLR.white, h="left", color=CLR.darkText) => ({ fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h}), border:mkBdrBot() });
  const sNUM   = (bg=CLR.white, color=CLR.darkText) => ({ fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h:"right"}), border:mkBdrBot() });
  const sPCT   = (bg=CLR.white, color=CLR.darkText) => ({ fill:mkFill(bg), font:mkFont({sz:9,color}), alignment:mkAln({h:"center"}), border:mkBdrBot() });
  const sTOT   = (color=CLR.gold, h="right") => ({ fill:mkFill(CLR.darkBg), font:mkFont({bold:true,color,sz:9}), alignment:mkAln({h}), border:mkBdrAll(CLR.gold) });
  const n = (v, s={}, z="#,##0") => mkC(v, s, z);
  const p = (v, s={}) => mkC(Math.floor(v * 1000) / 1000, s, "0.0%");  // floor before display
  const B  = (cnt) => Array(cnt).fill(null).map(()=>blank(CLR.darkBg));
  const BW = (cnt) => Array(cnt).fill(null).map(()=>blank());

  const buildSheet = (rows, merges=[], cols=[]) => {
    const ws = {}; let maxC = 0;
    rows.forEach((row, ri) => { (row||[]).forEach((cell, ci) => { if (!cell) return; ws[XLSX.utils.encode_cell({r:ri,c:ci})]=cell; maxC=Math.max(maxC,ci); }); });
    ws["!ref"] = XLSX.utils.encode_range({s:{r:0,c:0},e:{r:rows.length-1,c:maxC}});
    if (merges.length) ws["!merges"]=merges;
    if (cols.length)   ws["!cols"]=cols;
    return ws;
  };

  // ── Sheet 1: Ringkasan ──
  const cols1=[{wch:3},{wch:24},{wch:18},{wch:18},{wch:18},{wch:18},{wch:3}];
  const mg1=[], r1=[];
  r1.push(B(7));
  mg1.push({s:{r:1,c:1},e:{r:1,c:5}});
  r1.push([blank(CLR.darkBg), mkC("JF FINANCE — LAPORAN KEUANGAN BULANAN", sTITLE(CLR.darkBg,CLR.gold,14)), ...B(5)]);
  mg1.push({s:{r:2,c:1},e:{r:2,c:5}});
  r1.push([blank(CLR.darkBg), mkC(`Periode: ${mLabel}  |  Dibuat otomatis oleh JF Finance`, {fill:mkFill(CLR.darkBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}), ...B(5)]);
  r1.push(B(7));
  const metrics=[["💰 TOTAL PEMASUKAN",totalIncome,CLR.green],["💸 TOTAL PENGELUARAN",totalExpense,CLR.red],["📈 ALOKASI INVESTASI",totalInvOut,CLR.gold],["✅ SALDO BERSIH",netBersih,netBersih>=0?CLR.green:CLR.red]];
  r1.push([blank(CLR.darkBg),...metrics.map(([lbl,,col])=>mkC(lbl,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:8}),alignment:mkAln({h:"center"})})),blank(CLR.darkBg),blank(CLR.darkBg)]);
  r1.push([blank(CLR.darkBg),...metrics.map(([,val,col])=>n(val,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:col,sz:14}),alignment:mkAln({h:"center"})})),blank(CLR.darkBg),blank(CLR.darkBg)]);
  r1.push(B(7)); r1.push(BW(7));
  mg1.push({s:{r:8,c:1},e:{r:8,c:5}});
  r1.push([blank(),mkC("  📊  ALOKASI PENDAPATAN",sHDR()),...BW(5)]);
  r1.push([blank(),mkC("Kategori",sCOL()),mkC("Jumlah (Rp)",sCOL()),mkC("% dari Pendapatan",sCOL()),...BW(3)]);
  [["💸 Pengeluaran",totalExpense,CLR.red],["📈 Investasi",totalInvOut,CLR.gold],["✅ Saldo Bersih",netBersih,netBersih>=0?CLR.green:CLR.red]].forEach(([lbl,val,col],ai)=>{
    const bg=ai%2===0?CLR.lightBg:CLR.white;
    r1.push([blank(bg),mkC(lbl,{...sDATA(bg),font:mkFont({bold:true,sz:9})}),n(val,sNUM(bg)),p(totalIncome?val/totalIncome:0,sPCT(bg,col)),...BW(3)]);
  });
  r1.push([blank(CLR.darkBg),mkC("💡 SAVINGS RATE",{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:9}),alignment:mkAln({h:"left"})}),n(totalInvOut+netBersih,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:9}),alignment:mkAln({h:"right"})}),p(savingsRate,{fill:mkFill(CLR.darkBg),font:mkFont({bold:true,color:CLR.gold,sz:10}),alignment:mkAln({h:"center"})}),...B(3)]);
  XLSX.utils.book_append_sheet(wb, buildSheet(r1,mg1,cols1), "📋 Ringkasan");

  // ── Sheet 2: Pengeluaran per Kategori ──
  const catData=categories.filter(c=>c.type==="expense").map(cat=>({ cat, total:mTxs.filter(t=>t.type==="expense"&&t.categoryId===cat.id).reduce((a,t)=>a+t.amount,0) })).filter(d=>d.total>0).sort((a,b)=>b.total-a.total);
  const cols2=[{wch:3},{wch:26},{wch:20},{wch:18},{wch:18}];
  const mg2=[], r2=[];
  r2.push(B(5));
  mg2.push({s:{r:1,c:0},e:{r:1,c:4}});
  r2.push([mkC(`  💸  PENGELUARAN PER KATEGORI — ${mLabel}`, sTITLE(CLR.darkBg,CLR.gold,12)),...B(4)]);
  r2.push(B(5));
  r2.push([blank(),mkC("Kategori",sCOL()),mkC("Total (Rp)",sCOL()),mkC("% Pengeluaran",sCOL()),mkC("Peringkat",sCOL())]);
  catData.forEach(({cat,total},ci)=>{
    const bg=ci%2===0?CLR.lightBg:CLR.white;
    r2.push([blank(bg),mkC(`${cat.icon} ${cat.name}`,sDATA(bg)),n(total,{...sNUM(bg),font:mkFont({sz:9,color:ci===0?CLR.red:CLR.darkText,bold:ci===0})}),p(totalExpense?total/totalExpense:0,sPCT(bg)),mkC(`#${ci+1}`,{...sDATA(bg),alignment:mkAln({h:"center"})})]);
  });
  r2.push([blank(CLR.darkBg),mkC("TOTAL",sTOT(CLR.gold,"left")),n(totalExpense,sTOT()),...B(2)]);
  XLSX.utils.book_append_sheet(wb, buildSheet(r2,mg2,cols2), "💸 Pengeluaran");

  // ── Sheet 3: Budget vs Aktual ──
  const mBudgets=budgets.filter(b=>b.month===month&&b.year===year);
  const bvaData=categories.filter(c=>c.type==="expense"||c.type==="investasi").map(cat=>{
    const budgetAmt=mBudgets.find(b=>b.categoryId===cat.id)?.amount||0;
    const aktual=mTxs.filter(t=>t.categoryId===cat.id&&(t.type==="expense"||t.type==="investasi")).reduce((a,t)=>a+t.amount,0);
    return { cat, budgetAmt, aktual, selisih:budgetAmt-aktual, pct:budgetAmt>0?aktual/budgetAmt:null };
  }).filter(d=>d.budgetAmt>0||d.aktual>0);
  const expBva=bvaData.filter(d=>d.cat.type==="expense"), invBva=bvaData.filter(d=>d.cat.type==="investasi");
  const bvaColHdr=[blank(),mkC("Kategori",sCOL()),mkC("Budget (Rp)",sCOL()),mkC("Aktual (Rp)",sCOL()),mkC("Selisih (Rp)",sCOL()),mkC("% Realisasi",sCOL()),mkC("Status",sCOL())];
  const bvaRow=(d,bi,isInv=false)=>{
    const bg=bi%2===0?CLR.lightBg:CLR.white, over=d.selisih<0, noB=d.budgetAmt===0;
    const pct=Math.floor((d.pct||0)*1000)/1000;  // floor untuk konsistensi
    let status,stCol,selisihCol,pctCol;
    if(noB){status="Tidak Dianggarkan";stCol=CLR.muted;selisihCol=CLR.muted;pctCol=CLR.muted;}
    else if(isInv){
      status=over?"📈 Di Atas Target":pct>=1.0?"✅ Sesuai Target":pct>=0.9?"✅ On Track":"⚠️ Di Bawah Target";
      stCol=over?CLR.green:pct>=0.9?CLR.green:"F59E0B";
      selisihCol=over?CLR.green:CLR.red;pctCol=over?CLR.green:pct>=0.9?CLR.green:"F59E0B";
    } else {
      status=!over&&pct>=1.0?"✅ Tepat Anggaran":over?"⚠️ Over Budget":pct>=0.9?"🟡 Hampir Habis":"✅ On Track";
      stCol=over?CLR.red:pct>=0.9?"F59E0B":CLR.green;
      selisihCol=over?CLR.red:CLR.green;pctCol=over?CLR.red:pct>=0.9?"F59E0B":CLR.green;
    }
    return [blank(bg),mkC(`${d.cat.icon} ${d.cat.name}`,sDATA(bg)),n(d.budgetAmt,sNUM(bg)),n(d.aktual,sNUM(bg)),n(d.selisih,{...sNUM(bg),font:mkFont({sz:9,color:selisihCol})}),pct!==null?p(pct,sPCT(bg,pctCol)):mkC("—",{...sDATA(bg,"center"),font:mkFont({sz:9,color:CLR.muted})}),mkC(status,{...sDATA(bg),font:mkFont({sz:9,color:stCol,bold:true})})];
  };
  const cols3=[{wch:3},{wch:26},{wch:18},{wch:18},{wch:18},{wch:16},{wch:20}];
  const mg3=[], r3=[];
  r3.push(B(7));
  mg3.push({s:{r:1,c:0},e:{r:1,c:6}});
  r3.push([mkC(`  📊  BUDGET VS AKTUAL — ${mLabel}`, sTITLE(CLR.darkBg,CLR.gold,12)),...B(6)]);
  r3.push(B(7));
  mg3.push({s:{r:3,c:0},e:{r:3,c:6}});
  r3.push([mkC("  💸  PENGELUARAN",sHDR()),...Array(6).fill(blank(CLR.section))]);
  r3.push(bvaColHdr);
  expBva.forEach((d,bi)=>r3.push(bvaRow(d,bi)));
  const expB=expBva.reduce((a,d)=>a+d.budgetAmt,0),expA=expBva.reduce((a,d)=>a+d.aktual,0);
  r3.push([blank(CLR.darkBg),mkC("TOTAL",sTOT(CLR.gold,"left")),n(expB,sTOT()),n(expA,sTOT()),n(expB-expA,sTOT(expB-expA>=0?CLR.green:CLR.red)),expB>0?p(expA/expB,{...sTOT(expA>expB?CLR.red:CLR.green),alignment:mkAln({h:"center"})}):blank(CLR.darkBg),blank(CLR.darkBg)]);
  r3.push(BW(7));
  mg3.push({s:{r:r3.length,c:0},e:{r:r3.length,c:6}});
  r3.push([mkC("  📈  INVESTASI",sHDR()),...Array(6).fill(blank(CLR.section))]);
  r3.push(bvaColHdr);
  invBva.forEach((d,bi)=>r3.push(bvaRow(d,bi,true)));
  const invB=invBva.reduce((a,d)=>a+d.budgetAmt,0),invA=invBva.reduce((a,d)=>a+d.aktual,0);
  r3.push([blank(CLR.darkBg),mkC("TOTAL",sTOT(CLR.gold,"left")),n(invB,sTOT()),n(invA,sTOT()),n(invB-invA,sTOT(invB-invA<=0?CLR.green:CLR.red)),invB>0?p(invA/invB,{...sTOT(invA>invB?CLR.green:CLR.red),alignment:mkAln({h:"center"})}):blank(CLR.darkBg),blank(CLR.darkBg)]);
  XLSX.utils.book_append_sheet(wb, buildSheet(r3,mg3,cols3), "📊 Budget vs Aktual");

  // ── Sheet 4: History Transaksi ──
  const cols4=[{wch:3},{wch:13},{wch:14},{wch:22},{wch:28},{wch:30},{wch:18}];
  const mg4=[], r4=[];
  const typeMeta={income:{label:"💰 Masuk",color:CLR.green},expense:{label:"💸 Keluar",color:CLR.red},transfer:{label:"🔄 Transfer",color:CLR.blue},investasi:{label:"📈 Investasi",color:CLR.gold}};
  const sortedTxs=[...mTxs].sort((a,b)=>new Date(b.date)-new Date(a.date));
  r4.push(B(7));
  mg4.push({s:{r:1,c:0},e:{r:1,c:5}});
  r4.push([blank(CLR.darkBg),mkC(`  📜  HISTORY TRANSAKSI — ${mLabel}`, sTITLE(CLR.darkBg,CLR.gold,12)),...B(5)]);
  mg4.push({s:{r:2,c:0},e:{r:2,c:5}});
  r4.push([blank(CLR.darkBg),mkC(`  ${sortedTxs.length} transaksi, urut dari terbaru`,{fill:mkFill(CLR.darkBg),font:mkFont({sz:9,italic:true,color:CLR.muted}),alignment:mkAln({h:"left"})}),...B(5)]);
  r4.push(B(7));
  r4.push([blank(),mkC("Tanggal",sCOL()),mkC("Tipe",sCOL()),mkC("Kategori",sCOL()),mkC("Deskripsi",sCOL()),mkC("Catatan",sCOL()),mkC("Jumlah (Rp)",sCOL())]);
  sortedTxs.forEach((t,ti)=>{
    const bg=ti%2===0?CLR.lightBg:CLR.white;
    const cat=categories.find(c=>c.id===t.categoryId);
    const tm=typeMeta[t.type]||typeMeta.expense;
    const sign=t.type==="income"?"+":t.type==="transfer"?"⇄":"-";
    r4.push([blank(bg),mkC(new Date(t.date).toLocaleDateString("id-ID",{day:"2-digit",month:"short",year:"numeric"}),sDATA(bg)),mkC(tm.label,{...sDATA(bg,"center"),font:mkFont({sz:9,bold:true,color:tm.color})}),mkC(t.type==="transfer"?"—":cat?`${cat.icon} ${cat.name}`:"—",sDATA(bg)),mkC(t.description||"—",sDATA(bg)),mkC(t.notes||"—",{...sDATA(bg),font:mkFont({sz:9,italic:!t.notes,color:t.notes?CLR.darkText:CLR.muted})}),mkC(`${sign} ${Number(t.amount).toLocaleString("id-ID")}`,{fill:mkFill(bg),font:mkFont({sz:9,bold:true,color:tm.color}),alignment:mkAln({h:"right"}),border:mkBdrBot()})]);
  });
  r4.push([blank(CLR.darkBg),mkC(`TOTAL ${sortedTxs.length} TRANSAKSI`,sTOT(CLR.gold,"left")),...B(5)]);
  XLSX.utils.book_append_sheet(wb, buildSheet(r4,mg4,cols4), "📜 History Transaksi");

  XLSX.writeFile(wb, `JF_Finance_Laporan_${mLabel.replace(" ","_")}.xlsx`);
}

// ── Laporan ───────────────────────────────────────────────────────────────────
export default function Laporan({ transactions, categories, investments, budgets }) {
  const isMobile = useIsMobile();
  const now = new Date();
  const [viewMode, setViewMode] = useState("annual");        // "annual" | "monthly"
  const [vy, setVy] = useState(now.getFullYear());          // tahun (annual)
  const [vm, setVm] = useState(now.getMonth());             // bulan (monthly)
  const [vmy, setVmy] = useState(now.getFullYear());        // tahun untuk bulan

  // ── Annual data — via engine ──
  const { income: yearIncome, expense: yearExpense, investasi: yearInvOut, net: yearNet } =
    useMemo(() => getYearlySummary(transactions, vy), [transactions, vy]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyData = useMemo(() =>
    getMonthlyBreakdown(transactions, vy, MONTHS_SHORT).map(d => ({
      label: d.label, income: d.income, expense: d.expense, invest: d.investasi,
    })),
  [transactions, vy]); // eslint-disable-line react-hooks/exhaustive-deps

  const annualCatBreakdown = useMemo(() =>
    getExpenseByCategory(transactions, categories, 0, vy, "year"),
  [transactions, categories, vy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Monthly data — via engine ──
  const monthTxs = useMemo(() => filterByMonth(transactions, vm, vmy),
    [transactions, vm, vmy]); // eslint-disable-line react-hooks/exhaustive-deps

  const { income: mIncome, expense: mExpense, investasi: mInvOut, net: mNet } =
    useMemo(() => getMonthlySummary(transactions, vm, vmy), [transactions, vm, vmy]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthCatBreakdown = useMemo(() =>
    getExpenseByCategory(transactions, categories, vm, vmy, "month"),
  [transactions, categories, vm, vmy]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMonth = ()=>{ if(vm===0){setVm(11);setVmy(y=>y-1);}else setVm(m=>m-1); };
  const nextMonth = ()=>{ if(vm===11){setVm(0);setVmy(y=>y+1);}else setVm(m=>m+1); };

  const CatBars = ({data, total}) => data.length===0
    ? <div style={{padding:"20px 0",textAlign:"center",color:T.muted}}>Belum ada data</div>
    : data.map((cat,i) => (
      <div key={i} style={{marginBottom:i<data.length-1?12:0}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14}}>{cat.icon}</span>
            <span style={{color:T.text,fontSize:12}}>{cat.name}</span>
          </div>
          <div style={{fontSize:11,fontFamily:"monospace", fontVariantNumeric:"tabular-nums",color:T.muted}}>
            {fmtShort(cat.total)} <span style={{fontSize:10}}>({cat.pct.toFixed(1)}%)</span>
          </div>
        </div>
        <div style={{height:4,background:T.surface,borderRadius:3}}>
          <div style={{height:"100%",borderRadius:3,background:cat.color,width:`${cat.pct}%`}} />
        </div>
      </div>
    ));

  return (
    <div>
      {/* Mode toggle */}
      <div style={{display:"flex",background:T.surface,borderRadius:10,padding:4,marginBottom:18,border:`1px solid ${T.border}`}}>
        {[["annual","📅 Per Tahun"],["monthly","📆 Per Bulan"]].map(([m,l])=>(
          <button key={m} onClick={()=>setViewMode(m)} style={{flex:1,padding:"9px 0",borderRadius:7,cursor:"pointer",fontWeight:700,fontSize:13,border:"none",background:viewMode===m?T.card:T.surface,color:viewMode===m?T.text:T.muted,WebkitTapHighlightColor:"transparent",boxShadow:viewMode===m?`0 1px 4px rgba(0,0,0,0.3)`:"none"}}>{l}</button>
        ))}
      </div>

      {/* ── ANNUAL VIEW ── */}
      {viewMode === "annual" && (
        <>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <button onClick={()=>setVy(y=>y-1)} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:15,WebkitTapHighlightColor:"transparent"}}>◀</button>
            <div style={{color:T.text,fontWeight:800,fontSize:22,flex:1,textAlign:"center",fontFamily:"monospace", fontVariantNumeric:"tabular-nums"}}>{vy}</div>
            <button onClick={()=>setVy(y=>y+1)} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:15,WebkitTapHighlightColor:"transparent"}}>▶</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:14}}>
            <SumCard label="Pemasukan"   value={yearIncome}  color={T.green}  prefix="+" />
            <SumCard label="Pengeluaran" value={yearExpense} color={T.red} />
            <SumCard label="Investasi"   value={yearInvOut}  color={T.gold} />
            <SumCard label="Bersih"      value={yearNet}     color={yearNet>=0?T.green:T.red} prefix={yearNet>=0?"+":""} />
          </div>
          <Card style={{padding:16,marginBottom:14}}>
            <div style={{color:T.text,fontWeight:700,fontSize:13,marginBottom:14}}>📊 Tren Keuangan {vy}</div>
            <ResponsiveContainer width="100%" height={isMobile?180:220}>
              <ComposedChart data={monthlyData} margin={{top:4,right:4,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                <XAxis dataKey="label" tick={{fill:T.muted,fontSize:9}} axisLine={false} tickLine={false} />
                <YAxis tick={{fill:T.muted,fontSize:9}} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={50} />
                <Tooltip content={<CTooltip />} />
                <Bar     dataKey="income"    name="income"    fill={T.gold}  radius={[3,3,0,0]} maxBarSize={20} opacity={0.85} />
                <Line    dataKey="invest"    name="investasi" stroke={T.blue} strokeWidth={2} dot={{r:3,fill:T.blue,stroke:"#00000066",strokeWidth:1}} activeDot={{r:5}} type="linear" />
                <Line    dataKey="expense"   name="expense"   stroke={T.red}   strokeWidth={2} dot={{r:3,fill:T.red,  stroke:"#00000066",strokeWidth:1}} activeDot={{r:5}} type="linear" />
              </ComposedChart>
            </ResponsiveContainer>
            <div style={{display:"flex",gap:14,justifyContent:"center",marginTop:8,flexWrap:"wrap"}}>
              {[[T.gold,"Pemasukan","rect"],[T.blue,"Investasi","line"],[T.red,"Pengeluaran","line"]].map(([c,l,shape])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.muted}}>
                  {shape==="line"
                    ? <div style={{width:16,height:2,background:c,borderRadius:1,position:"relative"}}><div style={{width:5,height:5,borderRadius:"50%",background:c,position:"absolute",top:-1.5,left:5}}/></div>
                    : <div style={{width:10,height:10,borderRadius:2,background:c,opacity:0.85}}/>
                  }
                  {l}
                </div>
              ))}
            </div>
          </Card>
          <Card style={{padding:16,marginBottom:14}}>
            <div style={{color:T.text,fontWeight:700,fontSize:13,marginBottom:14}}>🏷️ Pengeluaran per Kategori {vy}</div>
            <CatBars data={annualCatBreakdown} />
          </Card>
          {/* Export button */}
          <button onClick={()=>exportLaporan(transactions,categories,investments,budgets,vy)}
            style={{width:"100%",background:T.green+"18",border:`1px solid ${T.green}33`,borderRadius:10,color:T.green,cursor:"pointer",padding:"13px 0",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8,WebkitTapHighlightColor:"transparent"}}>
            📥 Export Laporan {vy} ke Excel (.xlsx)
          </button>
        </>
      )}

      {/* ── MONTHLY VIEW ── */}
      {viewMode === "monthly" && (
        <>
          {/* Month navigator */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
            <button onClick={prevMonth} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,borderRadius:8,padding:"8px 12px",cursor:"pointer",fontSize:15,WebkitTapHighlightColor:"transparent"}}>◀</button>
            <div style={{color:T.text,fontWeight:700,fontSize:16,flex:1,textAlign:"center"}}>{MONTHS_FULL[vm]} {vmy}</div>
            <button onClick={nextMonth} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,borderRadius:8,padding:"8px 12px",cursor:"pointer",fontSize:15,WebkitTapHighlightColor:"transparent"}}>▶</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:14}}>
            <SumCard label="Pemasukan"   value={mIncome}  color={T.green}  prefix="+" />
            <SumCard label="Pengeluaran" value={mExpense} color={T.red} />
            <SumCard label="Investasi"   value={mInvOut}  color={T.gold} />
            <SumCard label="Bersih"      value={mNet}     color={mNet>=0?T.green:T.red} prefix={mNet>=0?"+":""} />
          </div>

          {/* Category breakdown */}
          <Card style={{padding:16,marginBottom:14}}>
            <div style={{color:T.text,fontWeight:700,fontSize:13,marginBottom:14}}>🏷️ Pengeluaran per Kategori</div>
            <CatBars data={monthCatBreakdown} />
          </Card>

          {/* Transaction list for this month */}
          <Card style={{padding:0}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,color:T.text,fontWeight:700,fontSize:13}}>
              📋 Semua Transaksi — {MONTHS_SHORT[vm]} {vmy} ({monthTxs.length})
            </div>
            {monthTxs.length === 0
              ? <div style={{padding:"32px 0",textAlign:"center",color:T.muted,fontSize:13}}>Tidak ada transaksi bulan ini</div>
              : [...monthTxs].sort((a,b)=>new Date(b.date)-new Date(a.date)).map((tx,idx)=>{
                const cat = categories.find(c=>c.id===tx.categoryId);
                const isTransfer = tx.type==="transfer";
                return (
                  <div key={tx.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:idx<monthTxs.length-1?`1px solid ${T.border}`:"none"}}>
                    <div style={{width:34,height:34,borderRadius:8,fontSize:18,flexShrink:0,background:isTransfer?T.blue+"1E":(cat?.color||T.muted)+"1E",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {isTransfer?"🔄":cat?.icon||"📌"}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:T.text,fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tx.description||cat?.name||"Transaksi"}</div>
                      <div style={{color:T.muted,fontSize:10,marginTop:1}}>
                        {cat?.name} · {new Date(tx.date).toLocaleDateString("id-ID",{day:"numeric",month:"short"})}
                      </div>
                    </div>
                    <div style={{color:isTransfer?T.blue:tx.type==="income"?T.green:tx.type==="investasi"?T.gold:T.red,fontFamily:"monospace", fontVariantNumeric:"tabular-nums",fontWeight:700,fontSize:12,whiteSpace:"nowrap"}}>
                      {isTransfer?"⇄":tx.type==="income"?"+":"-"}{fmtShort(tx.amount)}
                    </div>
                  </div>
                );
              })
            }
          </Card>
          {/* Export bulanan */}
          <button onClick={()=>exportLaporanBulanan(transactions,categories,investments,budgets,vm,vmy)}
            style={{width:"100%",background:T.gold+"18",border:`1px solid ${T.gold}33`,borderRadius:10,color:T.gold,cursor:"pointer",padding:"13px 0",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8,WebkitTapHighlightColor:"transparent",marginTop:12}}>
            📥 Export Laporan {MONTHS_FULL[vm]} {vmy} ke Excel (.xlsx)
          </button>
        </>
      )}
    </div>
  );
}
